/**
 * i18n.js — English/Telugu localization CONFIG for the Ticket Dashboard (Order Board).
 *
 * The reusable engine lives in src/i18n (`createI18n`). This file only supplies the
 * Ticket-Dashboard configuration — the language list (LANGS), the UI string bundles
 * (UI) and the curated menu-item dictionary (TERMS) — then re-exports the wired
 * helpers so components import `{ useLang, useAutoTranslate, LangProvider, … }` from
 * here unchanged (exactly like Daily Operations).
 *
 * Layers handled by the engine: (1) UI strings via `t()` with English fallback,
 * (2) item / ticket / customer names via `ln()` = curated TERMS → shared auto-translate
 * cache → as-typed, (3) dynamic fill of never-seen names (see translationEngine.js).
 * Language choice is per-DEVICE (localStorage 'td_lang') — a truck may run the board on
 * a shared screen while a manager's phone stays English.
 *
 * To extend: add a key to BOTH `UI.en` and `UI.te` (English-only is fine — it falls
 * back). Add a bilingual menu item as an `[english, telugu]` pair in TERMS.
 */
import { createI18n } from '../../i18n';

export const LANGS = [
  { id: 'en', label: 'EN', name: 'English' },
  { id: 'te', label: 'తె', name: 'తెలుగు' },
];

// ── UI chrome strings ────────────────────────────────────────────────────────
// Any key missing from `te` falls back to `en` (see the engine's `translate`), so a
// partial translation degrades gracefully rather than breaking.
const UI = {
  en: {
    // App shell / header
    'app.title': 'Order Board',
    'app.subtitle': 'Square KDS · Analytics · History',
    'app.live': 'Live',
    'app.dailyOps': 'Daily Ops',
    'app.dailyOpsTitle': 'Daily Operations — end-of-day restock & prep',
    'app.needHelp': 'Need Help — alert the owner',
    'app.refresh': 'Refresh orders',
    'app.focus': 'Focus mode (F)',
    'app.loading': 'Loading…',
    'app.loadingOrders': 'Loading orders…',
    'app.tabFailed': "This tab couldn't load. Check the connection and try again — or reload if the app was just updated.",
    'app.tabRetry': 'Try again',
    'app.tabReload': 'Reload app',
    'app.accessTitle': 'Access Restricted',
    'app.accessBody': 'Ticket dashboard is only available to owners, admins, and managers.',
    'lang.label': 'Language',
    'trunc.warn': 'Showing only the most recent {kept} orders — this range exceeds the {limit}-order limit, so the numbers below are incomplete. Pick a shorter range for accurate totals.',
    // Distribution strip — the spread an average hides
    'pct.p50': 'Median',
    'pct.p90': '90%',
    'pct.p99': '99%',
    'pct.worst': 'Worst',
    'pct.n': 'of {n} orders',
    'pct.prep': 'Prep time',
    'pct.finish': 'Order to done',
    // CSV export (the file itself stays English/canonical — only the button is translated)
    'csv.export': 'Export CSV',
    'csv.exportTitle': 'Download the filtered rows as a CSV for Excel',
    'csv.exportSummary': 'Export summary',
    'csv.exportSummaryTitle': 'Download these KPIs and the daily breakdown as a CSV',

    // Tab navigation (icons stay outside the string)
    'nav.kds': 'Live Orders',
    'nav.buffet': 'Buffet',
    'nav.alerts': 'Order Alerts',
    'nav.forecast': 'Prep Forecast',
    'nav.category': 'Category Sales',
    'nav.history': 'History',
    'nav.quality': 'Quality',
    'nav.tickets': 'All Tickets',
    'nav.analytics': 'Analytics',

    // Date-range presets (keyed by PRESETS[].key)
    'drp.today': 'Today',
    'drp.yesterday': 'Yesterday',
    'drp.7d': 'Last 7 Days',
    'drp.30d': 'Last 30 Days',
    'drp.custom': 'Custom',
    'drp.from': 'From',
    'drp.to': 'To',

    // Daily Ops slide-over drawer
    'drawer.title': 'Daily Operations',
    'drawer.fullPage': 'Full page',
    'drawer.close': 'Close Daily Operations',

    // Reusable count fragments
    'common.ticketsN': '{n} tickets',
    'common.itemsN': '{n} items',

    // ── KDS / Live Orders board ──────────────────────────────────────────────
    // Focus-corner + zoom controls
    'kds.fz.dops': 'Daily Operations — restock & prep',
    'kds.fz.help': 'Alert the owner',
    'kds.fz.summaryShow': 'Show bulk prep summary',
    'kds.fz.summaryHide': 'Hide item summary',
    'kds.exitFocusTitle': 'Exit focus mode (Esc)',
    'kds.exit': 'Exit',
    'kds.zoomOut': 'Zoom out',
    'kds.zoomIn': 'Zoom in',
    'kds.zoomReset': 'Reset zoom',
    // Density (how many tickets per row) and text size — two separate controls
    'kds.cols': 'Tickets per row',
    'kds.colsFewer': 'Fewer, wider tickets',
    'kds.colsMore': 'More tickets per row',
    'kds.colsAuto': 'Auto',
    'kds.colsReset': 'Back to automatic',
    'kds.text': 'Text size',
    'kds.textSmaller': 'Smaller text',
    'kds.textBigger': 'Bigger text',
    'kds.textReset': 'Reset text size',
    // Off-screen queue depth
    'kds.moreBelow': '{n} more below',
    'kds.moreBelowTitle': 'Tap to scroll down to the next tickets',
    // View pills
    'kds.pill.active': 'Active',
    'kds.pill.stale': 'Stale >90m',
    'kds.pill.completed': 'Completed',
    // New-order alerts
    'kds.alerts.off': 'Alerts',
    'kds.alerts.muted': 'Muted',
    'kds.alerts.on': 'Alerts On',
    'kds.alerts.titleOff': 'Enable new-order alerts (chime + browser notification)',
    'kds.alerts.titleMuted': 'Alerts on, sound muted — click to turn off',
    'kds.alerts.titleOn': 'New-order alerts on — click to mute the chime',
    // Chef view / profile / summary toggles
    'kds.chef.on': 'On',
    'kds.chef.off': 'Chef',
    'kds.chef.exitTitle': 'Exit Chef View',
    'kds.chef.enterTitle': 'Enter Chef View',
    'kds.profile': 'Profile',
    'kds.profileTitle': 'Chef profile',
    'kds.summary.on': 'Summary On',
    'kds.summary.off': 'Summary',
    'kds.summary.showTitle': 'Show summary',
    'kds.summary.hideTitle': 'Hide summary',
    'kds.hidden.showing': '👁 Showing {n} hidden',
    'kds.hidden.count': '👁 {n} Hidden',
    'kds.unhideAll': '✕ Unhide All',
    'kds.hiddenOverlay': 'Hidden',
    // Loader + empty states
    'kds.connecting': 'Connecting to Square POS…',
    'kds.empty.active.title': 'All clear!',
    'kds.empty.active.sub': 'No open orders right now.',
    'kds.empty.stale.title': 'No stale tickets',
    'kds.empty.stale.sub': 'All orders resolved within 90 minutes.',
    'kds.empty.completed.title': 'Nothing yet',
    'kds.empty.completed.sub': 'Completed orders will appear here.',
    'kds.cwin.recent': 'Completed in the last {hours} h · {n} earlier today (full day in 📋 History)',
    'kds.cwin.all': 'All {n} completed today',
    'kds.cwin.showAll': 'Show all today',
    'kds.cwin.showRecent': 'Last {hours} h only',
    // Bulk prep summary panel
    'kds.summary.heading': 'Bulk Prep Summary',
    'kds.summary.meta': '{tickets} tickets · {items} items',
    'kds.summary.clear': 'Clear highlight now',
    'kds.summary.chipTitle': 'On {n} tickets: {list}',

    // ── Ticket card (KdsCard) ────────────────────────────────────────────────
    'card.late': '⚠ LATE',
    'card.pickedUp': '📦 Picked Up',
    'card.payAtPickup': '💵 Pay at pickup',
    'card.paidOnline': '✅ Paid online',
    'card.callCustomer': 'Call customer',
    'card.noItems': 'No items',
    'card.itemDurAdded': 'Time since item was added',
    'card.itemDurCheck': 'Time to check this item',
    'card.itemWaiting': 'Waiting time for this item',
    'card.chefUndo': 'Undo chef prep done',
    'card.chefMark': 'Mark prep done',
    'card.chefPrepDone': 'Chef prep done',
    'card.assignedClick': 'Assigned — click to unassign',
    'card.assignItem': 'Assign item to chef',
    'card.frontDesk': '✅ Front Desk',
    'card.prepDoneSent': '👨‍🍳 Prep done • sent to front desk for packing',
    'card.prepProgress': '👨‍🍳 {done}/{total} items prep done',
    'card.hide': '👁 Hide',
    'card.hideTitle': 'Hide this ticket',
    'card.reopen': '↩ Reopen',
    'card.reopenTitle': 'Reopen this ticket',
    'card.completeTitle': 'Mark all items complete',
    'card.complete': '✓ Complete',
    'card.completeN': '✓ {done}/{total} complete',
    'card.assign': '👤 Assign',
    'card.assignAll': 'Assign all',
    'card.assignAllN': '👤 Assign All ({done}/{total})',
    'card.unassignAll': 'Unassign all',
    'card.unassignAllBtn': '↩ Unassign All',
    'card.assignAllComplete': 'All items are already complete',
    'card.noteLabel': 'Note:',

    // ── Sync bar ─────────────────────────────────────────────────────────────
    'sync.error': 'Square connection error: {error}',
    'sync.connecting': 'Connecting to Square…',
    'sync.openOrders': '{n} open orders',
    'sync.secAgo': '{n}s ago',
    'sync.liveBackup': 'live + {n}s backup',
    'sync.paused': 'paused',
    'sync.autoRefresh': 'Auto-refresh',
    'sync.auto': 'Auto',
    'sync.autoTitle': 'Follow store hours — polls while open, pauses when closed',
    'sync.pinTitle': 'Pin this interval — kept after a reload (best for a dedicated kitchen screen)',
    'sync.storeOpen': '● Open',
    'sync.storeClosed': '● Closed',
    'sync.refresh': '↻ Refresh',
    'sync.lanOn': 'LAN sync active — works without internet',
    'sync.lanOff': 'LAN sync offline (start local server)',
    'sync.offline': '⚠ OFFLINE MODE',

    // ── Completed-orders filters ─────────────────────────────────────────────
    'cf.duration': 'Duration',
    'cf.all': 'All',
    'cf.orderType': 'Order type',
    'cf.type.all': 'All',
    'cf.type.togo': 'To-go',
    'cf.type.online': 'Online',
    'cf.type.other': 'Other',
    'cf.sortLabel': 'Sort',
    'cf.sort.newest': 'Newest',
    'cf.sort.oldest': 'Oldest',
    'cf.sort.fastest': 'Fastest',
    'cf.sort.slowest': 'Slowest',
    'cf.reset': '✕ Reset',
    'cf.count': '{n} / {total} orders',

    // ── History tab ──────────────────────────────────────────────────────────
    'hist.loading': 'Loading history…',
    'hist.kpi.completed': 'Orders Completed',
    'hist.kpi.completedSub': 'filtered period',
    'hist.kpi.avgPrep': 'Avg Prep',
    'hist.kpi.tracked': '{n} tracked',
    'hist.kpi.avgFinish': 'Avg Finish',
    'hist.kpi.avgFinishSub': 'front desk complete',
    'hist.kpi.avgGap': 'Avg Pack Gap',
    'hist.kpi.chefTracked': '{n} chef-tracked',
    'hist.kpi.needChef': 'Need chef prep',
    'hist.kpi.fastest': 'Fastest Prep',
    'hist.kpi.slowest': 'Slowest Prep',
    'hist.kpi.chefLogged': 'Chef Logged',
    'hist.kpi.explicitPrep': '{n}% explicit prep',
    'hist.kpi.gap5': 'Gap >5 Min',
    'hist.kpi.handoffPct': '{n}% of chef handoffs',
    'hist.kpi.noHandoffs': 'No handoffs yet',
    'hist.noCompleted': 'No completed orders in this period.',
    'hist.prepDist': '📊 Prep Time Distribution',
    'hist.noPrepTimings': 'No prep timings in this period',
    'hist.gapDist': '🧾 Pack Gap Distribution',
    'hist.noGaps': 'No chef handoff gaps in this period',
    'hist.buckets': '⏱️ Completion Time Buckets',
    'hist.bucketFiltering': 'Filtering table below — click again to clear',
    'hist.bucketHint': 'Click a bucket to see those orders',
    'hist.bucketTitle': '{count} orders completed in {label}',
    'hist.searchPlaceholder': 'Search order name, items…',
    'hist.minFinal': 'Min final (min)',
    'hist.maxFinal': 'Max final (min)',
    'hist.clearBucket': 'Clear bucket filter',
    'hist.clearFilters': '✕ Clear filters',
    'hist.ordersN': '{n} orders',
    'hist.noMatch': 'No orders match your filters.',
    'hist.col.order': 'Order',
    'hist.col.items': 'Items',
    'hist.col.prepReady': 'Prep Ready',
    'hist.col.completed': 'Completed',
    'hist.col.prepTime': 'Prep Time',
    'hist.col.packGap': 'Pack Gap',
    'hist.col.timeTaken': 'Time Taken',
    'hist.col.quality': 'Quality',
    'hist.chip.chef': 'chef',
    'hist.chip.atComplete': 'at complete',
    'hist.prev': '‹ Prev',
    'hist.next': 'Next ›',
    'hist.pageInfo': 'Page {page} of {total} — {n} orders',

    // ── All Tickets tab ──────────────────────────────────────────────────────
    'tickets.showing': 'Showing',
    'tickets.completed': 'Completed',
    'tickets.avgTime': 'Avg Time',
    'tickets.searchPlaceholder': 'Search tickets, items, customer…',
    'tickets.status.all': 'All Statuses',
    'tickets.status.open': 'Open',
    'tickets.status.inprogress': 'In Progress',
    'tickets.status.completed': 'Completed',
    'tickets.status.stale': 'Stale',
    'tickets.status.cancelled': 'Cancelled',
    'tickets.allCats': 'All Categories',
    'tickets.noMatch': 'No tickets match your filters.',
    'tickets.col.ticket': 'Ticket',
    'tickets.col.status': 'Status',
    'tickets.col.category': 'Category',
    'tickets.col.items': 'Items',
    'tickets.col.created': 'Created',
    'tickets.col.timeTaken': 'Time Taken',
    'tickets.general': 'General',
    'tickets.pageInfo': 'Page {page} of {total} — {n} tickets',
    // Status badge (shared)
    'badge.open': 'Open',
    'badge.inprogress': 'In Progress',
    'badge.completed': 'Completed',
    'badge.stale': 'Stale',
    'badge.cancelled': 'Cancelled',
    // Items cell (shared History + All Tickets)
    'items.expandHint': 'Click to see prep times & notes',
    'items.collapseHint': 'Click to collapse',
    'items.showAllTimings': '⏱ Show all item timings',
    'items.hideAllTimings': '⏱ Hide item timings',
    'items.hasMods': 'Has modifiers',
    'items.none': '—',

    // ── Buffet board (Sunday dosa buffet) ────────────────────────────────────
    // View pills + day stats
    'bf.viewNew': 'New Order',
    'bf.viewOrders': 'Orders',
    'bf.viewDone': 'Completed',
    'bf.toMake': 'to make',
    'bf.made': 'made',
    'bf.open': 'open',
    // Board controls
    'bf.zoomOut': 'Zoom out',
    'bf.zoomIn': 'Zoom in',
    'bf.zoomReset': 'Reset zoom',
    // Density (how many orders per row) and text size — two separate controls
    'bf.cols': 'Orders per row',
    'bf.colsFewer': 'Fewer, wider order cards',
    'bf.colsMore': 'More orders per row',
    'bf.colsAuto': 'Auto',
    'bf.colsReset': 'Back to automatic',
    'bf.text': 'Text size',
    'bf.textSmaller': 'Smaller text',
    'bf.textBigger': 'Bigger text',
    'bf.textReset': 'Reset text size',
    // Off-screen queue depth
    'bf.moreBelow': '{n} more below',
    'bf.moreBelowTitle': 'Tap to scroll down to the next orders',
    'bf.sun': 'Sun',
    'bf.dark': 'Dark',
    'bf.sunOn': 'High-contrast mode for sunlight/glare',
    'bf.sunOff': 'Switch back to dark board',
    'bf.maximize': 'Maximize',
    'bf.minimize': 'Minimize',
    // Section headings + empty states
    'bf.activeOrders': 'Active Orders',
    'bf.buffetDosas': 'Buffet Dosas',
    'bf.regularOrders': 'Regular Orders',
    'bf.regularSub': 'dosas & breads from Live Orders',
    'bf.regularTag': 'Regular',
    'bf.loading': 'Loading…',
    'bf.emptyActive': 'No active orders yet — save one on the left.',
    'bf.emptyBuffet': 'No active buffet orders.',
    'bf.emptyDone': 'No completed orders yet today.',
    'bf.noItems': 'No items',
    'bf.note': 'Note:',
    // New-order entry panel
    'bf.customerName': 'Customer name',
    'bf.required': '* required',
    'bf.namePlaceholder': 'Type name, or tap mic 🎤',
    'bf.clear': 'Clear',
    'bf.flavor': 'Flavor',
    'bf.addons': 'Add-ons',
    'bf.custom': 'Custom',
    'bf.customPlaceholder': 'Custom dosa name',
    'bf.add': 'Add',
    'bf.addTitle': 'Add one {name} to this order',
    'bf.qtyLess': 'One less {name}',
    'bf.qtyMore': 'One more {name}',
    'bf.thisOrder': 'This order',
    'bf.dosaN': '{n} dosa',
    'bf.dosasN': '{n} dosas',
    'bf.pickHint': 'Pick a dosa and tap Add.',
    'bf.remove': 'Remove',
    'bf.saving': 'Saving…',
    'bf.needName': 'Enter customer name',
    'bf.needDosa': 'Add a dosa',
    'bf.saveOrder': 'Save Order ({n})',
    'bf.savedFor': '✓ Saved order for {name}',
    'bf.micListening': 'Listening… tap to stop',
    'bf.micIdle': 'Tap and speak',
    // Order cards
    'bf.leftN': '{n} left',
    'bf.madeN': '{n} made',
    'bf.tapCooked': 'Tap when cooked',
    'bf.guest': 'Guest',
    'bf.done': 'Done',
    'bf.removeOrder': 'Remove order',
    'bf.reopen': 'Reopen',
    // Completed summary
    'bf.ordersDone': 'Orders done',
    'bf.dosasMade': 'Dosas made',
    'bf.dosaTypes': 'Dosa types',
    'bf.mostPopular': 'Most popular',
    'bf.breakdown': 'Breakdown by dosa',
    // Failures
    'bf.saveFailed': 'Could not save order: {msg}',
    'bf.updateFailed': 'Update failed: {msg}',
  },
  te: {
    // App shell / header
    'app.title': 'ఆర్డర్ బోర్డు',
    'app.subtitle': 'స్క్వేర్ KDS · విశ్లేషణలు · చరిత్ర',
    'app.live': 'లైవ్',
    'app.dailyOps': 'డైలీ ఆప్స్',
    'app.dailyOpsTitle': 'డైలీ ఆపరేషన్స్ — రోజు చివరి రీస్టాక్ & ప్రిప్',
    'app.needHelp': 'సహాయం కావాలి — యజమానికి తెలియజేయి',
    'app.refresh': 'ఆర్డర్లను రిఫ్రెష్ చేయి',
    'app.focus': 'ఫోకస్ మోడ్ (F)',
    'app.loading': 'లోడ్ అవుతోంది…',
    'app.loadingOrders': 'ఆర్డర్లు లోడ్ అవుతున్నాయి…',
    'app.tabFailed': 'ఈ ట్యాబ్ లోడ్ కాలేదు. కనెక్షన్ చూసి మళ్లీ ప్రయత్నించండి — యాప్ ఇప్పుడే అప్‌డేట్ అయితే రీలోడ్ చేయండి.',
    'app.tabRetry': 'మళ్లీ ప్రయత్నించు',
    'app.tabReload': 'యాప్ రీలోడ్ చేయి',
    'app.accessTitle': 'యాక్సెస్ నిషేధించబడింది',
    'app.accessBody': 'టికెట్ డాష్‌బోర్డు యజమానులు, అడ్మిన్‌లు మరియు మేనేజర్‌లకు మాత్రమే అందుబాటులో ఉంటుంది.',
    'lang.label': 'భాష',
    'trunc.warn': 'ఇటీవలి {kept} ఆర్డర్లు మాత్రమే చూపుతోంది — ఈ శ్రేణి {limit} ఆర్డర్ల పరిమితిని దాటింది, కాబట్టి కింది సంఖ్యలు అసంపూర్ణం. ఖచ్చితమైన మొత్తాల కోసం చిన్న శ్రేణిని ఎంచుకోండి.',
    // Distribution strip — the spread an average hides
    'pct.p50': 'మధ్యస్థం',
    'pct.p90': '90%',
    'pct.p99': '99%',
    'pct.worst': 'అత్యధికం',
    'pct.n': '{n} ఆర్డర్లలో',
    'pct.prep': 'ప్రిప్ సమయం',
    'pct.finish': 'ఆర్డర్ నుండి పూర్తి',
    // CSV export (the file itself stays English/canonical — only the button is translated)
    'csv.export': 'CSV డౌన్‌లోడ్',
    'csv.exportTitle': 'ఫిల్టర్ చేసిన వరుసలను ఎక్సెల్ కోసం CSV గా డౌన్‌లోడ్ చేయి',
    'csv.exportSummary': 'సారాంశం డౌన్‌లోడ్',
    'csv.exportSummaryTitle': 'ఈ KPIలు మరియు రోజువారీ వివరాలను CSV గా డౌన్‌లోడ్ చేయి',

    // Tab navigation
    'nav.kds': 'లైవ్ ఆర్డర్లు',
    'nav.buffet': 'బఫే',
    'nav.alerts': 'ఆర్డర్ అలర్ట్‌లు',
    'nav.forecast': 'ప్రిప్ ఫోర్‌కాస్ట్',
    'nav.category': 'కేటగిరీ సేల్స్',
    'nav.history': 'చరిత్ర',
    'nav.quality': 'నాణ్యత',
    'nav.tickets': 'అన్ని టికెట్లు',
    'nav.analytics': 'విశ్లేషణలు',

    // Date-range presets
    'drp.today': 'ఈరోజు',
    'drp.yesterday': 'నిన్న',
    'drp.7d': 'గత 7 రోజులు',
    'drp.30d': 'గత 30 రోజులు',
    'drp.custom': 'అనుకూలం',
    'drp.from': 'నుండి',
    'drp.to': 'వరకు',

    // Daily Ops slide-over drawer
    'drawer.title': 'డైలీ ఆపరేషన్స్',
    'drawer.fullPage': 'పూర్తి పేజీ',
    'drawer.close': 'డైలీ ఆపరేషన్స్ మూసివేయి',

    // Reusable count fragments
    'common.ticketsN': '{n} టికెట్లు',
    'common.itemsN': '{n} వస్తువులు',

    // ── KDS / Live Orders board ──────────────────────────────────────────────
    'kds.fz.dops': 'డైలీ ఆపరేషన్స్ — రీస్టాక్ & ప్రిప్',
    'kds.fz.help': 'యజమానికి తెలియజేయి',
    'kds.fz.summaryShow': 'బల్క్ ప్రిప్ సారాంశం చూపించు',
    'kds.fz.summaryHide': 'ఐటమ్ సారాంశం దాచు',
    'kds.exitFocusTitle': 'ఫోకస్ మోడ్ నుండి నిష్క్రమించు (Esc)',
    'kds.exit': 'నిష్క్రమించు',
    'kds.zoomOut': 'జూమ్ అవుట్',
    'kds.zoomIn': 'జూమ్ ఇన్',
    'kds.zoomReset': 'జూమ్ రీసెట్',
    'kds.cols': 'వరుసకు టికెట్లు',
    'kds.colsFewer': 'తక్కువ, వెడల్పు టికెట్లు',
    'kds.colsMore': 'వరుసకు ఎక్కువ టికెట్లు',
    'kds.colsAuto': 'ఆటో',
    'kds.colsReset': 'ఆటోమేటిక్‌కి తిరిగి',
    'kds.text': 'టెక్స్ట్ సైజు',
    'kds.textSmaller': 'చిన్న అక్షరాలు',
    'kds.textBigger': 'పెద్ద అక్షరాలు',
    'kds.textReset': 'టెక్స్ట్ సైజు రీసెట్',
    'kds.moreBelow': 'కింద మరో {n}',
    'kds.moreBelowTitle': 'తదుపరి టికెట్లకు స్క్రోల్ చేయడానికి నొక్కండి',
    'kds.pill.active': 'యాక్టివ్',
    'kds.pill.stale': 'పాతవి >90ని',
    'kds.pill.completed': 'పూర్తయినవి',
    'kds.alerts.off': 'అలర్ట్‌లు',
    'kds.alerts.muted': 'మ్యూట్',
    'kds.alerts.on': 'అలర్ట్‌లు ఆన్',
    'kds.alerts.titleOff': 'కొత్త ఆర్డర్ అలర్ట్‌లను ఎనేబుల్ చేయి (చైమ్ + బ్రౌజర్ నోటిఫికేషన్)',
    'kds.alerts.titleMuted': 'అలర్ట్‌లు ఆన్, శబ్దం మ్యూట్ — ఆఫ్ చేయడానికి క్లిక్ చేయి',
    'kds.alerts.titleOn': 'కొత్త ఆర్డర్ అలర్ట్‌లు ఆన్ — చైమ్ మ్యూట్ చేయడానికి క్లిక్ చేయి',
    'kds.chef.on': 'ఆన్',
    'kds.chef.off': 'చెఫ్',
    'kds.chef.exitTitle': 'చెఫ్ వ్యూ నుండి నిష్క్రమించు',
    'kds.chef.enterTitle': 'చెఫ్ వ్యూలోకి ప్రవేశించు',
    'kds.profile': 'ప్రొఫైల్',
    'kds.profileTitle': 'చెఫ్ ప్రొఫైల్',
    'kds.summary.on': 'సారాంశం ఆన్',
    'kds.summary.off': 'సారాంశం',
    'kds.summary.showTitle': 'సారాంశం చూపించు',
    'kds.summary.hideTitle': 'సారాంశం దాచు',
    'kds.hidden.showing': '👁 {n} దాచినవి చూపిస్తోంది',
    'kds.hidden.count': '👁 {n} దాచినవి',
    'kds.unhideAll': '✕ అన్నీ చూపించు',
    'kds.hiddenOverlay': 'దాచబడింది',
    'kds.connecting': 'స్క్వేర్ POS కి కనెక్ట్ అవుతోంది…',
    'kds.empty.active.title': 'అంతా క్లియర్!',
    'kds.empty.active.sub': 'ప్రస్తుతం ఓపెన్ ఆర్డర్లు లేవు.',
    'kds.empty.stale.title': 'పాత టికెట్లు లేవు',
    'kds.empty.stale.sub': 'అన్ని ఆర్డర్లు 90 నిమిషాల్లో పరిష్కరించబడ్డాయి.',
    'kds.empty.completed.title': 'ఇంకా ఏమీ లేదు',
    'kds.empty.completed.sub': 'పూర్తయిన ఆర్డర్లు ఇక్కడ కనిపిస్తాయి.',
    'kds.cwin.recent': 'గత {hours} గంటల్లో పూర్తయినవి · ఈరోజు అంతకుముందువి {n} (పూర్తి రోజు 📋 చరిత్రలో)',
    'kds.cwin.all': 'ఈరోజు పూర్తయినవి మొత్తం {n}',
    'kds.cwin.showAll': 'ఈరోజు అన్నీ చూపు',
    'kds.cwin.showRecent': 'గత {hours} గంటలు మాత్రమే',
    'kds.summary.heading': 'బల్క్ ప్రిప్ సారాంశం',
    'kds.summary.meta': '{tickets} టికెట్లు · {items} వస్తువులు',
    'kds.summary.clear': 'హైలైట్ ఇప్పుడు క్లియర్ చేయి',
    'kds.summary.chipTitle': '{n} టికెట్లపై: {list}',

    // ── Ticket card (KdsCard) ────────────────────────────────────────────────
    'card.late': '⚠ ఆలస్యం',
    'card.pickedUp': '📦 తీసుకెళ్లారు',
    'card.payAtPickup': '💵 పికప్ వద్ద చెల్లించండి',
    'card.paidOnline': '✅ ఆన్‌లైన్‌లో చెల్లించారు',
    'card.callCustomer': 'కస్టమర్‌కు కాల్ చేయి',
    'card.noItems': 'వస్తువులు లేవు',
    'card.itemDurAdded': 'వస్తువు జోడించిన నుండి సమయం',
    'card.itemDurCheck': 'ఈ వస్తువును చెక్ చేయడానికి పట్టిన సమయం',
    'card.itemWaiting': 'ఈ వస్తువు వేచి ఉన్న సమయం',
    'card.chefUndo': 'చెఫ్ ప్రిప్ పూర్తి రద్దు చేయి',
    'card.chefMark': 'ప్రిప్ పూర్తయినట్లు గుర్తించు',
    'card.chefPrepDone': 'చెఫ్ ప్రిప్ పూర్తయింది',
    'card.assignedClick': 'కేటాయించబడింది — తీసివేయడానికి క్లిక్ చేయి',
    'card.assignItem': 'వస్తువును చెఫ్‌కు కేటాయించు',
    'card.frontDesk': '✅ ఫ్రంట్ డెస్క్',
    'card.prepDoneSent': '👨‍🍳 ప్రిప్ పూర్తయింది • ప్యాకింగ్ కోసం ఫ్రంట్ డెస్క్‌కు పంపబడింది',
    'card.prepProgress': '👨‍🍳 {done}/{total} వస్తువుల ప్రిప్ పూర్తయింది',
    'card.hide': '👁 దాచు',
    'card.hideTitle': 'ఈ టికెట్‌ను దాచు',
    'card.reopen': '↩ మళ్లీ తెరువు',
    'card.reopenTitle': 'ఈ టికెట్‌ను మళ్లీ తెరువు',
    'card.completeTitle': 'అన్ని వస్తువులు పూర్తయినట్లు గుర్తించు',
    'card.complete': '✓ పూర్తి',
    'card.completeN': '✓ {done}/{total} పూర్తి',
    'card.assign': '👤 కేటాయించు',
    'card.assignAll': 'అన్నీ కేటాయించు',
    'card.assignAllN': '👤 అన్నీ కేటాయించు ({done}/{total})',
    'card.unassignAll': 'అన్నీ తీసివేయి',
    'card.unassignAllBtn': '↩ అన్నీ తీసివేయి',
    'card.assignAllComplete': 'అన్ని వస్తువులు ఇప్పటికే పూర్తయ్యాయి',
    'card.noteLabel': 'గమనిక:',

    // ── Sync bar ─────────────────────────────────────────────────────────────
    'sync.error': 'స్క్వేర్ కనెక్షన్ లోపం: {error}',
    'sync.connecting': 'స్క్వేర్‌కు కనెక్ట్ అవుతోంది…',
    'sync.openOrders': '{n} ఓపెన్ ఆర్డర్లు',
    'sync.secAgo': '{n}సె క్రితం',
    'sync.liveBackup': 'లైవ్ + {n}సె బ్యాకప్',
    'sync.paused': 'పాజ్ చేయబడింది',
    'sync.autoRefresh': 'ఆటో-రిఫ్రెష్',
    'sync.auto': 'ఆటో',
    'sync.autoTitle': 'స్టోర్ వేళలను అనుసరించు — తెరిచినప్పుడు పోల్, మూసినప్పుడు ఆపు',
    'sync.pinTitle': 'ఈ విరామాన్ని పిన్ చేయి — రీలోడ్ తర్వాత కూడా ఉంటుంది (కిచెన్ స్క్రీన్‌కు ఉత్తమం)',
    'sync.storeOpen': '● తెరిచి ఉంది',
    'sync.storeClosed': '● మూసివేయబడింది',
    'sync.refresh': '↻ రిఫ్రెష్',
    'sync.lanOn': 'LAN సింక్ యాక్టివ్ — ఇంటర్నెట్ లేకుండా పనిచేస్తుంది',
    'sync.lanOff': 'LAN సింక్ ఆఫ్‌లైన్ (లోకల్ సర్వర్ ప్రారంభించండి)',
    'sync.offline': '⚠ ఆఫ్‌లైన్ మోడ్',

    // ── Completed-orders filters ─────────────────────────────────────────────
    'cf.duration': 'వ్యవధి',
    'cf.all': 'అన్నీ',
    'cf.orderType': 'ఆర్డర్ రకం',
    'cf.type.all': 'అన్నీ',
    'cf.type.togo': 'టేకవే',
    'cf.type.online': 'ఆన్‌లైన్',
    'cf.type.other': 'ఇతర',
    'cf.sortLabel': 'క్రమం',
    'cf.sort.newest': 'కొత్తవి',
    'cf.sort.oldest': 'పాతవి',
    'cf.sort.fastest': 'వేగవంతమైనవి',
    'cf.sort.slowest': 'నెమ్మదిగా',
    'cf.reset': '✕ రీసెట్',
    'cf.count': '{n} / {total} ఆర్డర్లు',

    // ── History tab ──────────────────────────────────────────────────────────
    'hist.loading': 'చరిత్ర లోడ్ అవుతోంది…',
    'hist.kpi.completed': 'పూర్తయిన ఆర్డర్లు',
    'hist.kpi.completedSub': 'ఫిల్టర్ చేసిన కాలం',
    'hist.kpi.avgPrep': 'సగటు ప్రిప్',
    'hist.kpi.tracked': '{n} ట్రాక్ చేయబడ్డాయి',
    'hist.kpi.avgFinish': 'సగటు ఫినిష్',
    'hist.kpi.avgFinishSub': 'ఫ్రంట్ డెస్క్ పూర్తి',
    'hist.kpi.avgGap': 'సగటు ప్యాక్ గ్యాప్',
    'hist.kpi.chefTracked': '{n} చెఫ్-ట్రాక్',
    'hist.kpi.needChef': 'చెఫ్ ప్రిప్ అవసరం',
    'hist.kpi.fastest': 'వేగవంతమైన ప్రిప్',
    'hist.kpi.slowest': 'నెమ్మదైన ప్రిప్',
    'hist.kpi.chefLogged': 'చెఫ్ లాగ్',
    'hist.kpi.explicitPrep': '{n}% స్పష్టమైన ప్రిప్',
    'hist.kpi.gap5': 'గ్యాప్ >5 నిమి',
    'hist.kpi.handoffPct': 'చెఫ్ హ్యాండ్‌ఆఫ్‌లలో {n}%',
    'hist.kpi.noHandoffs': 'ఇంకా హ్యాండ్‌ఆఫ్‌లు లేవు',
    'hist.noCompleted': 'ఈ కాలంలో పూర్తయిన ఆర్డర్లు లేవు.',
    'hist.prepDist': '📊 ప్రిప్ సమయ పంపిణీ',
    'hist.noPrepTimings': 'ఈ కాలంలో ప్రిప్ సమయాలు లేవు',
    'hist.gapDist': '🧾 ప్యాక్ గ్యాప్ పంపిణీ',
    'hist.noGaps': 'ఈ కాలంలో చెఫ్ హ్యాండ్‌ఆఫ్ గ్యాప్‌లు లేవు',
    'hist.buckets': '⏱️ పూర్తి సమయ విభాగాలు',
    'hist.bucketFiltering': 'దిగువ టేబుల్‌ను ఫిల్టర్ చేస్తోంది — క్లియర్ చేయడానికి మళ్లీ క్లిక్ చేయి',
    'hist.bucketHint': 'ఆ ఆర్డర్లను చూడటానికి ఒక విభాగాన్ని క్లిక్ చేయి',
    'hist.bucketTitle': '{label}లో {count} ఆర్డర్లు పూర్తయ్యాయి',
    'hist.searchPlaceholder': 'ఆర్డర్ పేరు, వస్తువులు వెతకండి…',
    'hist.minFinal': 'కనిష్ట ఫైనల్ (నిమి)',
    'hist.maxFinal': 'గరిష్ట ఫైనల్ (నిమి)',
    'hist.clearBucket': 'విభాగ ఫిల్టర్ క్లియర్ చేయి',
    'hist.clearFilters': '✕ ఫిల్టర్లు క్లియర్ చేయి',
    'hist.ordersN': '{n} ఆర్డర్లు',
    'hist.noMatch': 'మీ ఫిల్టర్లకు సరిపోయే ఆర్డర్లు లేవు.',
    'hist.col.order': 'ఆర్డర్',
    'hist.col.items': 'వస్తువులు',
    'hist.col.prepReady': 'ప్రిప్ సిద్ధం',
    'hist.col.completed': 'పూర్తయింది',
    'hist.col.prepTime': 'ప్రిప్ సమయం',
    'hist.col.packGap': 'ప్యాక్ గ్యాప్',
    'hist.col.timeTaken': 'పట్టిన సమయం',
    'hist.col.quality': 'నాణ్యత',
    'hist.chip.chef': 'చెఫ్',
    'hist.chip.atComplete': 'పూర్తయినప్పుడు',
    'hist.prev': '‹ మునుపటి',
    'hist.next': 'తదుపరి ›',
    'hist.pageInfo': 'పేజీ {page} / {total} — {n} ఆర్డర్లు',

    // ── All Tickets tab ──────────────────────────────────────────────────────
    'tickets.showing': 'చూపిస్తోంది',
    'tickets.completed': 'పూర్తయినవి',
    'tickets.avgTime': 'సగటు సమయం',
    'tickets.searchPlaceholder': 'టికెట్లు, వస్తువులు, కస్టమర్ వెతకండి…',
    'tickets.status.all': 'అన్ని స్థితులు',
    'tickets.status.open': 'ఓపెన్',
    'tickets.status.inprogress': 'ప్రోగ్రెస్‌లో',
    'tickets.status.completed': 'పూర్తయింది',
    'tickets.status.stale': 'పాతది',
    'tickets.status.cancelled': 'రద్దు చేయబడింది',
    'tickets.allCats': 'అన్ని కేటగిరీలు',
    'tickets.noMatch': 'మీ ఫిల్టర్లకు సరిపోయే టికెట్లు లేవు.',
    'tickets.col.ticket': 'టికెట్',
    'tickets.col.status': 'స్థితి',
    'tickets.col.category': 'కేటగిరీ',
    'tickets.col.items': 'వస్తువులు',
    'tickets.col.created': 'సృష్టించబడింది',
    'tickets.col.timeTaken': 'పట్టిన సమయం',
    'tickets.general': 'జనరల్',
    'tickets.pageInfo': 'పేజీ {page} / {total} — {n} టికెట్లు',
    // Status badge (shared)
    'badge.open': 'ఓపెన్',
    'badge.inprogress': 'ప్రోగ్రెస్‌లో',
    'badge.completed': 'పూర్తయింది',
    'badge.stale': 'పాతది',
    'badge.cancelled': 'రద్దు చేయబడింది',
    // Items cell (shared History + All Tickets)
    'items.expandHint': 'ప్రిప్ సమయాలు & గమనికలు చూడటానికి క్లిక్ చేయి',
    'items.collapseHint': 'కుదించడానికి క్లిక్ చేయి',
    'items.showAllTimings': '⏱ అన్ని వస్తువుల సమయాలు చూపించు',
    'items.hideAllTimings': '⏱ వస్తువుల సమయాలు దాచు',
    'items.hasMods': 'మాడిఫైయర్లు ఉన్నాయి',
    'items.none': '—',

    // ── Buffet board (Sunday dosa buffet) ────────────────────────────────────
    // View pills + day stats
    'bf.viewNew': 'కొత్త ఆర్డర్',
    'bf.viewOrders': 'ఆర్డర్లు',
    'bf.viewDone': 'పూర్తయినవి',
    'bf.toMake': 'చేయాలి',
    'bf.made': 'చేసినవి',
    'bf.open': 'ఓపెన్',
    // Board controls
    'bf.zoomOut': 'చిన్నదిగా',
    'bf.zoomIn': 'పెద్దదిగా',
    'bf.zoomReset': 'రీసెట్ చేయి',
    'bf.cols': 'వరుసకు ఆర్డర్లు',
    'bf.colsFewer': 'తక్కువ, వెడల్పు కార్డులు',
    'bf.colsMore': 'వరుసకు ఎక్కువ ఆర్డర్లు',
    'bf.colsAuto': 'ఆటో',
    'bf.colsReset': 'ఆటోమేటిక్‌కి తిరిగి',
    'bf.text': 'టెక్స్ట్ సైజు',
    'bf.textSmaller': 'చిన్న అక్షరాలు',
    'bf.textBigger': 'పెద్ద అక్షరాలు',
    'bf.textReset': 'టెక్స్ట్ సైజు రీసెట్',
    'bf.moreBelow': 'కింద మరో {n}',
    'bf.moreBelowTitle': 'తదుపరి ఆర్డర్లకు స్క్రోల్ చేయడానికి నొక్కండి',
    'bf.sun': 'ఎండ',
    'bf.dark': 'డార్క్',
    'bf.sunOn': 'ఎండలో స్పష్టంగా కనిపించే మోడ్',
    'bf.sunOff': 'తిరిగి డార్క్ బోర్డుకి',
    'bf.maximize': 'పెద్దది చేయి',
    'bf.minimize': 'చిన్నది చేయి',
    // Section headings + empty states
    'bf.activeOrders': 'ప్రస్తుత ఆర్డర్లు',
    'bf.buffetDosas': 'బఫే దోశలు',
    'bf.regularOrders': 'రెగ్యులర్ ఆర్డర్లు',
    'bf.regularSub': 'లైవ్ ఆర్డర్ల నుండి దోశలు & రొట్టెలు',
    'bf.regularTag': 'రెగ్యులర్',
    'bf.loading': 'లోడ్ అవుతోంది…',
    'bf.emptyActive': 'ఇంకా ఆర్డర్లు లేవు — ఎడమ వైపు ఒకటి సేవ్ చేయండి.',
    'bf.emptyBuffet': 'ప్రస్తుతం బఫే ఆర్డర్లు లేవు.',
    'bf.emptyDone': 'ఈ రోజు ఇంకా పూర్తయిన ఆర్డర్లు లేవు.',
    'bf.noItems': 'వస్తువులు లేవు',
    'bf.note': 'గమనిక:',
    // New-order entry panel
    'bf.customerName': 'కస్టమర్ పేరు',
    'bf.required': '* తప్పనిసరి',
    'bf.namePlaceholder': 'పేరు టైప్ చేయండి, లేదా మైక్ నొక్కండి 🎤',
    'bf.clear': 'క్లియర్',
    'bf.flavor': 'రుచి',
    'bf.addons': 'యాడ్-ఆన్‌లు',
    'bf.custom': 'కస్టమ్',
    'bf.customPlaceholder': 'కస్టమ్ దోశ పేరు',
    'bf.add': 'జోడించు',
    'bf.addTitle': 'ఈ ఆర్డర్‌కి ఒక {name} జోడించు',
    'bf.qtyLess': '{name} ఒకటి తగ్గించు',
    'bf.qtyMore': '{name} ఒకటి పెంచు',
    'bf.thisOrder': 'ఈ ఆర్డర్',
    'bf.dosaN': '{n} దోశ',
    'bf.dosasN': '{n} దోశలు',
    'bf.pickHint': 'ఒక దోశ ఎంచుకుని జోడించు నొక్కండి.',
    'bf.remove': 'తీసివేయి',
    'bf.saving': 'సేవ్ అవుతోంది…',
    'bf.needName': 'కస్టమర్ పేరు నమోదు చేయండి',
    'bf.needDosa': 'ఒక దోశ జోడించండి',
    'bf.saveOrder': 'ఆర్డర్ సేవ్ చేయి ({n})',
    'bf.savedFor': '✓ {name} కోసం ఆర్డర్ సేవ్ అయింది',
    'bf.micListening': 'వింటోంది… ఆపడానికి నొక్కండి',
    'bf.micIdle': 'నొక్కి మాట్లాడండి',
    // Order cards
    'bf.leftN': '{n} మిగిలాయి',
    'bf.madeN': '{n} చేసాం',
    'bf.tapCooked': 'కాల్చాక నొక్కండి',
    'bf.guest': 'గెస్ట్',
    'bf.done': 'పూర్తయింది',
    'bf.removeOrder': 'ఆర్డర్ తీసివేయి',
    'bf.reopen': 'మళ్ళీ తెరువు',
    // Completed summary
    'bf.ordersDone': 'పూర్తయిన ఆర్డర్లు',
    'bf.dosasMade': 'చేసిన దోశలు',
    'bf.dosaTypes': 'దోశ రకాలు',
    'bf.mostPopular': 'ఎక్కువ అమ్ముడైనది',
    'bf.breakdown': 'దోశల వారీగా వివరాలు',
    // Failures
    'bf.saveFailed': 'ఆర్డర్ సేవ్ చేయలేకపోయాం: {msg}',
    'bf.updateFailed': 'అప్‌డేట్ విఫలమైంది: {msg}',
  },
};

// ── Curated menu / kitchen-item dictionary ───────────────────────────────────
// Bidirectional [english, telugu]. Used by `ln()` for ticket line-item names; unknown
// free-text names fall through to the shared auto-translate cache, then as-typed.
// Policy (same as DailyOps): transliterate dishes (Biryani → బిర్యానీ), translate plain
// ingredients (Eggs → గుడ్లు), leave sizes/brands alone.
const TERMS = [
  // Dosas & tiffins
  ['Dosa', 'దోశ'], ['Masala Dosa', 'మసాలా దోశ'], ['Plain Dosa', 'ప్లెయిన్ దోశ'],
  ['Ghee Dosa', 'నెయ్యి దోశ'], ['Onion Dosa', 'ఉల్లి దోశ'],
  // Term lookup is exact, so each spelling we actually ship needs its own row —
  // the menu poster says "Uthappam", this table historically said "Uttapam".
  ['Uttapam', 'ఉత్తప్పం'], ['Uthappam', 'ఉత్తప్పం'],
  ['Onion Uttapam', 'ఉల్లి ఉత్తప్పం'], ['Onion Uthappam', 'ఉల్లి ఉత్తప్పం'],
  ['Idly', 'ఇడ్లీ'], ['Idli', 'ఇడ్లీ'], ['Ghee Idly', 'నెయ్యి ఇడ్లీ'], ['Ghee Chitti Idly', 'నెయ్యి చిట్టి ఇడ్లీ'],
  ['Vada', 'వడ'], ['Mysore Bonda', 'మైసూర్ బోండా'], ['Punugulu', 'పునుగులు'], ['Bonda', 'బోండా'],
  ['Poori', 'పూరి'], ['Pesarattu', 'పెసరట్టు'], ['Masala Pesarattu', 'మసాలా పెసరట్టు'],
  ['Ragi', 'రాగి'], ['Ragi Dosa', 'రాగి దోశ'],
  // Sunday dosa buffet — flavor bases + composed combos "[Ghee] [Flavor] [Onion] Dosa".
  // Plain/Onion/Ghee Dosa are already covered above; these add the rest so every one of
  // the 16 buffet combinations resolves offline via ln() in Telugu mode.
  ['Plain', 'ప్లెయిన్'], ['Podi', 'పొడి'], ['Karampodi', 'కారంపొడి'], ['Karam', 'కారం'],
  ['Ghee', 'నెయ్యి'], ['Onion', 'ఉల్లి'],
  ['Podi Dosa', 'పొడి దోశ'], ['Karampodi Dosa', 'కారంపొడి దోశ'], ['Karam Dosa', 'కారం దోశ'],
  ['Podi Onion Dosa', 'పొడి ఉల్లి దోశ'], ['Karampodi Onion Dosa', 'కారంపొడి ఉల్లి దోశ'], ['Karam Onion Dosa', 'కారం ఉల్లి దోశ'],
  ['Ghee Onion Dosa', 'నెయ్యి ఉల్లి దోశ'], ['Ghee Podi Dosa', 'నెయ్యి పొడి దోశ'],
  ['Ghee Karampodi Dosa', 'నెయ్యి కారంపొడి దోశ'], ['Ghee Karam Dosa', 'నెయ్యి కారం దోశ'],
  ['Ghee Podi Onion Dosa', 'నెయ్యి పొడి ఉల్లి దోశ'], ['Ghee Karampodi Onion Dosa', 'నెయ్యి కారంపొడి ఉల్లి దోశ'],
  ['Ghee Karam Onion Dosa', 'నెయ్యి కారం ఉల్లి దోశ'],
  // Biryanis & rice
  ['Biryani', 'బిర్యానీ'], ['Chicken Biryani', 'చికెన్ బిర్యానీ'], ['Veg Biryani', 'వెజ్ బిర్యానీ'],
  ['Boneless Biryani', 'బోన్‌లెస్ బిర్యానీ'], ['Chicken Ghee Roast Pulav', 'చికెన్ నెయ్యి రోస్ట్ పులావ్'],
  ['Pulav', 'పులావ్'], ['Fried Rice', 'ఫ్రైడ్ రైస్'], ['Egg Fried Rice', 'ఎగ్ ఫ్రైడ్ రైస్'],
  ['Chicken Fried Rice', 'చికెన్ ఫ్రైడ్ రైస్'], ['Curd Rice', 'పెరుగన్నం'],
  // Curries & mains
  ['Chicken Curry', 'చికెన్ కర్రీ'], ['Chicken 65', 'చికెన్ 65'], ['Gobi 65', 'గోబీ 65'],
  ['Butter Chicken', 'బటర్ చికెన్'], ['Paneer', 'పనీర్'], ['Dal', 'పప్పు'], ['Sambar', 'సాంబార్'],
  // Breads. Lookup is exact, so each spelling the griddle board can surface
  // (see GRIDDLE_PATTERNS in buffetUtils) needs its own row.
  ['Naan', 'నాన్'], ['Butter Naan', 'బటర్ నాన్'], ['Garlic Naan', 'వెల్లుల్లి నాన్'], ['Roti', 'రొట్టె'],
  ['Chapati', 'చపాతీ'], ['Chapathi', 'చపాతీ'], ['Paratha', 'పరాఠా'],
  ['Parotta', 'పరోటా'], ['Parratta', 'పరోటా'], ['Paratta', 'పరోటా'],
  ['Kerala Parotta', 'కేరళ పరోటా'], ['Ghee Paratha', 'నెయ్యి పరాఠా'],
  ['Pulka', 'పుల్కా'], ['Phulka', 'పుల్కా'],
  // Street bites & chaat
  ['Samosa', 'సమోసా'], ['Pakora', 'పకోడీ'], ['Pani Puri', 'పానీ పూరి'], ['Bhel Puri', 'భేల్ పూరి'],
  ['Chaat', 'చాట్'], ['Mirchi', 'మిర్చి'],
  // Drinks & desserts
  ['Tea', 'టీ'], ['Coffee', 'కాఫీ'], ['Bru Coffee', 'బ్రూ కాఫీ'], ['Mango Lassi', 'మ్యాంగో లస్సీ'],
  ['Lassi', 'లస్సీ'], ['Buttermilk', 'మజ్జిగ'], ['Gulab Jamun', 'గులాబ్ జామూన్'], ['Kulfi', 'కుల్ఫీ'],
  // Generic
  ['Water', 'నీళ్ళు'], ['Custom Amount', 'కస్టమ్ మొత్తం'],
];

// ── Wire this config into the reusable engine (src/i18n) ─────────────────────
// All the localization logic (provider, fallback, term index, auto-translate) lives in
// the shared factory; here we just hand it the Ticket-Dashboard languages, strings & terms.
const i18n = createI18n({
  languages: LANGS,
  resources: UI,
  terms: TERMS,
  termLangs: ['en', 'te'],   // TERMS pairs are [english, telugu]
  storageKey: 'td_lang',     // per-device choice
});

export const LangProvider = i18n.LangProvider;
export const useLang = i18n.useLang;
export const useAutoTranslate = i18n.useAutoTranslate;
export const useEngine = i18n.useEngine;                 // used by translationEngine.js (Firestore)
export const localizeName = i18n.localize;               // (name, lang, cache?) → localized/as-typed
export const isKnownTerm = i18n.isKnownTerm;
export const normTerm = i18n.norm;
